import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

interface SheetOverlayProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> {
  withinContainer?: boolean;
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  SheetOverlayProps
>(({ className, withinContainer, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      withinContainer ? "ui-absolute ui-inset-0" : "ui-fixed ui-inset-0",
      "ui-z-50 ui-bg-black/80 data-[state=open]:ui-animate-in data-[state=closed]:ui-animate-out data-[state=closed]:ui-fade-out-0 data-[state=open]:ui-fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "ui-z-50 ui-gap-4 ui-bg-background ui-shadow-lg ui-transition ui-ease-out data-[state=closed]:ui-duration-300 data-[state=open]:ui-duration-200 data-[state=open]:ui-animate-in data-[state=closed]:ui-animate-out ui-outline-none focus:ui-outline-none focus-visible:ui-outline-none",
  {
    variants: {
      side: {
        top: "ui-inset-x-0 ui-top-0 ui-border-b data-[state=closed]:ui-slide-out-to-top data-[state=open]:ui-slide-in-from-top",
        bottom:
          "ui-inset-x-0 ui-bottom-0 data-[state=closed]:ui-slide-out-to-bottom data-[state=open]:ui-slide-in-from-bottom",
        left: "ui-inset-y-0 ui-left-0 ui-h-full ui-w-full ui-border-r data-[state=closed]:ui-slide-out-to-left data-[state=open]:ui-slide-in-from-left ",
        right:
          "ui-inset-y-0 ui-right-0 ui-h-full ui-border-l ui-w-full data-[state=closed]:ui-slide-out-to-right data-[state=open]:ui-slide-in-from-right ",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  container?: HTMLElement | DocumentFragment | null;
  withinContainer?: boolean;
  overlayClassName?: string;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      className,
      children,
      container,
      withinContainer,
      overlayClassName,
      ...props
    },
    ref
  ) => (
    <SheetPortal container={container ?? undefined}>
      {withinContainer ? (
        <div className={cn("ui-absolute ui-inset-0 ui-z-50 ui-bg-black/80", overlayClassName)} />
      ) : (
        <SheetOverlay className={overlayClassName} />
      )}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          withinContainer ? "ui-absolute" : "ui-fixed",
          sheetVariants({ side }),
          className,
          "ui-rounded-t-2xl ui-overflow-hidden scrollbar-hide"
        )}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "ui-flex ui-flex-col ui-space-y-2 ui-text-center sm:ui-text-left",
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "ui-flex ui-flex-col-reverse sm:ui-flex-row sm:ui-justify-end sm:ui-space-x-2",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("ui-text-lg ui-font-semibold ui-text-foreground", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("ui-text-sm ui-text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};